// client.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import * as CryptoJS from 'crypto-js';
import { setting } from '../../../setting';
import { ClientSetting, ClientSettingAppAuth } from '../interfaces/clientSetting.interface';


@Injectable({
    providedIn: 'root'
})
export class ClientService {
    private clientDataSubject = new BehaviorSubject<ClientSetting | null>(null);
    public clientData$: Observable<ClientSetting | null> = this.clientDataSubject.asObservable();

    private isLoadingSubject = new BehaviorSubject<boolean>(false);
    public isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();

    private clientData: ClientSetting | null = null;
    private isInitialized = false;

    constructor(private http: HttpClient) {
        this.initializeClient();
    }

    /**
     * Inicializa los datos del cliente al cargar la aplicación
     */
    private initializeClient(): void {
        if (this.isInitialized) return;

        this.isLoadingSubject.next(true);

        const clientData = this.getDecryptedData();

        if (clientData) {
            this.clientData = clientData;
            this.clientDataSubject.next(clientData);
            this.isLoadingSubject.next(false);

            // Verificar si hay actualizaciones
            this.checkForUpdates(clientData);
        } else {
            // Si no hay datos o la clave cambió, obtener nuevos
            this.fetchClientData();
        }
    }

    /**
     * Obtiene los datos del cliente desde el servidor
     */
    private fetchClientData(): void {
        this.isLoadingSubject.next(true);

        let params: HttpParams = new HttpParams();
        params = params.set('name', setting.name);
    // return this._http.get(this.authApiUrl + 'getClientsSettingAppAuth', { params: params })

        this.http.get<ClientSetting>(`${setting.authApiUrl}getClientsSettingAppAuth` , { params: params }).subscribe({
            next: (response: ClientSetting) => {
                if (response) {
                    // Guardar encriptado
                    const encryptedData = CryptoJS.AES.encrypt(
                        JSON.stringify(response),
                        setting.key_local_storage
                    ).toString();

                    localStorage.setItem(`${setting.name}`, encryptedData);

                    this.clientData = response;
                    this.clientDataSubject.next(response);
                    this.isLoadingSubject.next(false);
                    this.isInitialized = true;
                }
            },
            error: () => {
                this.isLoadingSubject.next(false);
            }
        });
    }

    /**
     * Verifica si hay actualizaciones en el servidor
     */
    private checkForUpdates(clientData: ClientSetting): void {
        const clientStorageId = clientData.id || 0;
        const clientStorageUpdatedAt = clientData.updated_at || null;

        if (clientStorageId > 0 && clientStorageUpdatedAt) {
          let params: HttpParams = new HttpParams();
            params = params.set('name', setting.name);

            this.http.get(`${setting.authApiUrl}clients-updates`, { params: params }).subscribe({
                next: (res: any) => {
                    if (res && res !== clientStorageUpdatedAt) {
                        // Hay actualización, obtener nuevos datos
                        this.fetchClientData();
                    }
                },
                error: () => {
                    // Si hay error, mantener los datos actuales
                }
            });
        }
    }

    /**
     * Desencripta los datos del localStorage
     */
    private getDecryptedData(): ClientSetting | null {
        const encryptedData = localStorage.getItem(`${setting.name}`);

        if (!encryptedData) {
            return null;
        }

        try {
            // Intentar parsear como JSON (caso de datos ya desencriptados)
            const parsed = JSON.parse(encryptedData);
            if (parsed?.id) {
                return parsed;
            }
        } catch {
            // No es JSON, continuar con desencriptación
        }

        try {
            const bytes = CryptoJS.AES.decrypt(encryptedData, setting.key_local_storage);
            const decryptedString = bytes.toString(CryptoJS.enc.Utf8);

            if (!decryptedString) {
                // Si falla, limpiar localStorage y obtener nuevos datos
                localStorage.removeItem(`${setting.name}`);
                return null;
            }

            return JSON.parse(decryptedString);
        } catch (error) {
            // Si falla la desencriptación (clave incorrecta), limpiar y obtener nuevos
            console.warn('Clave de encriptación incorrecta o datos corruptos. Obteniendo nuevos...');
            localStorage.removeItem(`${setting.name}`);
            return null;
        }
    }

    /**
     * Obtiene los datos del cliente (sincrónico)
     */
    getClientData(): ClientSetting | null {
        return this.clientData;
    }

    /**
     * Obtiene los datos del cliente (asincrónico)
     */
    getClientDataAsync(): Observable<ClientSetting | null> {
        return this.clientData$;
    }

    /**
     * Verifica si los datos están cargando
     */
    isLoading(): Observable<boolean> {
        return this.isLoading$;
    }

    /**
     * Fuerza la recarga de los datos del cliente
     */
    refreshClientData(): void {
        localStorage.removeItem(`${setting.name}`);
        this.fetchClientData();
    }

    /**
     * Obtiene una sección específica de la configuración del cliente
     */
    getSection<T>(sectionName: keyof ClientSettingAppAuth): T | null {
        if (!this.clientData?.clients_setting_app_auth) {
            return null;
        }

        try {
            const sectionData = this.clientData.clients_setting_app_auth[sectionName];
            if (typeof sectionData === 'string') {
                return JSON.parse(sectionData) as T;
            }
            return sectionData as unknown as T;
        } catch {
            return null;
        }
    }
}
